import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { DETAIL_STATUS, DETAIL_STATUS_BAR, DetailStatusBar, DetailStatusChip } from '../../../common/DetailStatusChip';
import { firstLinked, getInvoiceDetailUrl } from '../../../common/LinkedDocumentLink';
import { useGetShopConfig } from '../../../services/core/settings';
import { MILEAGE_UNIT, normalizeWorkOrderStatus, PRICING_TYPES, WORK_ORDER_AUTHORIZATION_TEXT, WORK_ORDER_STATUS } from '../../../utility/Constant';
import { recordHasPartsOrPackages } from '../../../utility/helpers/taxCalculation';
import { useProvinceTaxGroup } from '../../../utility/hooks/useProvinceTaxGroup';
import DetailPageHeaderInfoColumns from '../../Invoice/invoiceCommonComponents/DetailPageHeaderInfoColumns';
import InvoiceRecommandationSection from '../../Invoice/invoiceCommonComponents/InvoiceRecommandationSection';
import { WorkOrderStatusChip } from '../WorkOrders';
import WorkOrderFeesTable from './WorkOrderFeesTable';
import WorkOrderPaymentDetails from './WorkOrderPaymentDetails';
import WorkOrderServiceTable from './WorkOrderServiceTable';

/**
 * Read-only Work Order document, shared by every detail view.
 *
 * The header columns and notes section are the invoice's components, reused as-is. The fees table,
 * service table and totals are work-order-owned copies of their invoice counterparts, so they can
 * diverge without touching the invoice.
 */
const WorkOrderDocument = ({ workOrderData, shopName }) => {
    const { data: shopConfigData } = useGetShopConfig();
    const provinceTaxGroup = useProvinceTaxGroup();

    const mileageUnit = shopConfigData?.config?.mileageUnit === MILEAGE_UNIT.MILES ? MILEAGE_UNIT.MILES : MILEAGE_UNIT.KM;

    const documentHasPartsOrPackages = useMemo(() => recordHasPartsOrPackages(workOrderData), [workOrderData]);

    const authorizationText = workOrderData?.authorizationMessage || WORK_ORDER_AUTHORIZATION_TEXT(shopName);

    const status = normalizeWorkOrderStatus(workOrderData?.status);
    // Authorized and Cancelled show a full-width bar instead of a chip.
    const isAuthorized = status === WORK_ORDER_STATUS.AUTHORIZED;
    const isCancelled = status === WORK_ORDER_STATUS.CANCELLED;
    const linkedInvoice = firstLinked(workOrderData?.linkedInvoiceId);

    /** Chips under the title, by status. Invoiced shows two: Authorized + Invoiced (links to the invoice). */
    const statusChips = (() => {
        switch (status) {
            case WORK_ORDER_STATUS.OPEN:
                return [<DetailStatusChip key="open" status={DETAIL_STATUS.OPEN} />];
            case WORK_ORDER_STATUS.REAPPROVAL:
                return [<DetailStatusChip key="reapprove" status={DETAIL_STATUS.REAPPROVE} />];
            case WORK_ORDER_STATUS.INVOICED:
                return [
                    <DetailStatusChip key="authorized" status={DETAIL_STATUS.AUTHORIZED} />,
                    <DetailStatusChip
                        key="invoiced"
                        status={DETAIL_STATUS.INVOICED}
                        to={linkedInvoice?._id ? getInvoiceDetailUrl(linkedInvoice) : undefined}
                    />,
                ];
            case WORK_ORDER_STATUS.DRAFT:
                return [<DetailStatusChip key="draft" status={DETAIL_STATUS.DRAFT} />];
            default:
                // Any status added later falls back to the listing chip.
                return [<WorkOrderStatusChip key="status" status={status} />];
        }
    })();

    const services = Array.isArray(workOrderData?.services) ? workOrderData.services : [];
    const hasFees = Array.isArray(workOrderData?.fees) && workOrderData.fees.length > 0;

    return (
        <Grid container padding={'32px'} margin={'32px 0'} boxShadow={2} gap={2} sx={{ bgcolor: 'white' }}>
            {/* Title + number */}
            <Grid container item spacing={2} justifyContent="space-between" paddingRight={2}>
                <Grid item lg={8} md={12} sm={12} xs={12}>
                    <Typography variant="h3" fontWeight={400}>
                        Work Order
                    </Typography>
                    {!isAuthorized && !isCancelled && (
                        <Stack direction="row" gap={2} mt={1} sx={{ flexWrap: 'wrap' }}>
                            {statusChips}
                        </Stack>
                    )}
                </Grid>

                <Grid item>
                    <Stack spacing={0}>
                        <Typography variant="body1" fontWeight={'500'} fontSize={'24px'} lineHeight={'133.33%'}>
                            {workOrderData?.workOrderNumber}
                        </Typography>
                        {workOrderData?.serviceAdvisor?.name && (
                            <Typography variant="body1" fontWeight={400} lineHeight={'150%'} mt={1}>
                                Service Advisor: {workOrderData?.serviceAdvisor?.name}
                            </Typography>
                        )}
                    </Stack>
                </Grid>
            </Grid>

            {/* Authorized / Cancelled — a full-width bar under the title row, in place of the chip */}
            {isAuthorized && <DetailStatusBar status={DETAIL_STATUS_BAR.AUTHORIZED} />}
            {isCancelled && <DetailStatusBar status={DETAIL_STATUS_BAR.CANCELLED} />}

            <Divider style={{ width: '100%' }} />

            <Grid container item justifyItems={'space-between'}>
                <DetailPageHeaderInfoColumns
                    documentType="workOrder"
                    workOrderDate={workOrderData?.workOrderDate}
                    authorizedDate={workOrderData?.authorizedDate}
                    customer={workOrderData?.customer}
                    customerPO={workOrderData?.customerPO}
                    vehicle={workOrderData?.vehicle}
                    mileageUnit={mileageUnit}
                    estimateMileageOnly
                    estimateSingleOdo={workOrderData?.mileage ?? workOrderData?.odoDroppedOff}
                />

                {/* Internal shop memo */}
                {workOrderData?.note && (
                    <Grid item xs={12} mt={2}>
                        <Typography
                            style={{
                                color: 'rgba(0, 0, 0, 0.87)',
                                fontSize: '16px',
                                fontWeight: 500,
                                lineHeight: '150%',
                                letterSpacing: '0.15px',
                            }}
                        >
                            Internal Shop Memo
                        </Typography>
                        <Typography
                            style={{
                                color: 'rgba(0, 0, 0, 0.65)',
                                marginTop: '8px',
                                fontSize: '16px',
                                fontWeight: 400,
                                lineHeight: '24px',
                                letterSpacing: '0.15px',
                            }}
                        >
                            {workOrderData?.note}
                        </Typography>
                    </Grid>
                )}

                {/* Services */}
                {services.map((item, index) => (
                    <WorkOrderServiceTable
                        key={item?._id || index}
                        item={item}
                        workOrderData={workOrderData}
                        provinceTaxGroup={provinceTaxGroup}
                        documentHasPartsOrPackages={documentHasPartsOrPackages}
                    />
                ))}

                {/* Fees */}
                {hasFees && <WorkOrderFeesTable invoiceData={workOrderData} />}

                {/* Notes & Recommendations */}
                {workOrderData?.recommendationNotes && <InvoiceRecommandationSection invoiceData={workOrderData} />}

                {/* Authorization + totals */}
                {/* spacing={4} puts marginTop:-32px on the container; `pt` does not cancel that, so
                    this block used to sit 32px over the fees table and swallow its hover. */}
                <Grid container item justifyContent={'space-between'} spacing={4} sx={{ mt: 0 }}>
                    <Grid item lg={6} md={12} sm={12} xs={12}>
                        <Stack spacing={1}>
                            <Typography fontSize={16} variant="body1" fontWeight={'500'}>
                                Authorization
                            </Typography>
                            <Typography
                                variant="body1"
                                fontWeight={400}
                                fontSize={16}
                                whiteSpace="pre-line"
                                style={{ color: 'rgba(0, 0, 0, 0.65)', wordBreak: 'break-word' }}
                            >
                                {authorizationText}
                            </Typography>
                        </Stack>
                    </Grid>

                    <Grid item lg={6} md={12} sm={12} xs={12}>
                        <WorkOrderPaymentDetails workOrderData={workOrderData} />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default WorkOrderDocument;
